import { useState, FormEvent } from 'react';

interface MeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MeetingModal({ isOpen, onClose }: MeetingModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d65eush3fhhvgn1ui730', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
          setSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gray-900 px-8 py-6 relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <h3 className="text-2xl font-bold text-white pr-8">Schedule Executive Meeting with Covasant</h3>
          <p className="text-gray-300 text-sm mt-2">Meet our enterprise AI transformation experts at Booth TFG 28</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-green-600 text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Meeting Scheduled!</h4>
              <p className="text-gray-600">Covasant looks forward to meeting you at Booth TFG 28. You'll receive confirmation within 24 hours.</p>
            </div>
          ) : (
            <form id="meeting-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input 
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input 
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="john.smith@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <input 
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="+971 XX XXX XXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title *</label>
                  <input 
                    type="text"
                    name="job_title"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="Chief Information Officer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Company *</label>
                  <input 
                    type="text"
                    name="company"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                    placeholder="Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Day *</label>
                  <select 
                    name="preferred_day"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  >
                    <option value="">Select a day</option>
                    <option value="feb-11">February 11, 2026</option>
                    <option value="feb-12">February 12, 2026</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Time *</label>
                  <select 
                    name="preferred_time"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
                  >
                    <option value="">Select a time</option>
                    <option value="9-10">9:00 AM - 10:00 AM</option>
                    <option value="10-11">10:00 AM - 11:00 AM</option>
                    <option value="11-12">11:00 AM - 12:00 PM</option>
                    <option value="12-13">12:00 PM - 1:00 PM</option>
                    <option value="14-15">2:00 PM - 3:00 PM</option>
                    <option value="15-16">3:00 PM - 4:00 PM</option>
                    <option value="16-17">4:00 PM - 5:00 PM</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">AI Scaling Challenges to Discuss</label>
                  <textarea 
                    name="topics"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base resize-none"
                    placeholder="What pilot-to-production challenges would you like to discuss with Covasant? (max 500 characters)"
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 text-white py-4 rounded-lg font-bold text-base hover:bg-gray-800 hover:shadow-lg transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? 'Scheduling...' : 'Schedule Meeting with Covasant'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
